import React from 'react';
import type { DomainMainMenuItem } from '@creator/sdk/modules/config/config.model';
import Icon from '../../basic-components/Icon/Icon';
import Link from '../../basic-components/Link/Link';
import { propsEqualityFn } from '../../utils/object-utils';
import type { FC, ReactNode, MouseEvent as ReactMouseEvent } from 'react';
import { memo, useState } from 'react';
import { cn } from '../../utils/ui';

export interface MenuItemModel extends DomainMainMenuItem {
    onClick?: (e: ReactMouseEvent) => void;
}

interface MenuItemProps {
    item: MenuItemModel;
    className?: string;
    children?: ReactNode;
    isLoading?: boolean;
    onClick?: (e: ReactMouseEvent) => void;
}

const MenuItem: FC<MenuItemProps> = props => {
    const { item, className = '', children, onClick } = props;
    const [isOpen, setIsOpen] = useState(false);

    // Determine if there is nested content either as children or as nested items.
    const hasNestedContent = children || (item.children && item.children.length > 0);

    // When clicking the menu item title:
    // - If nested content exists and no URL is provided, toggle open/closed.
    // - Otherwise, execute the provided onClick callback (if any).
    const handleItemClick = (e: ReactMouseEvent) => {
        // Only toggle when there is nested content and no URL.
        if (hasNestedContent && !item.url) {
            e.stopPropagation();
            setIsOpen(prev => !prev);
        } else if (item?.onClick) {
            e.stopPropagation();
            item.onClick(e);
        }
        props.onClick?.(e);
    };

    // Render the title as a <Link> if a URL is provided, else as a clickable <span>.
    const renderTitle = () => {
        if (item.url) {
            return (
                <Link to={item.url} className="menu-item-link">
                    {item.title}
                </Link>
            );
        }
        return <span onClick={handleItemClick}>{item.title}</span>;
    };

    // Render the toggle button if nested content is present.
    const renderToggleButton = () => {
        return (
            <div
                className="p-1"
                onClick={e => {
                    // Prevent any parent click events (like from a Link) from firing.
                    e.stopPropagation();
                    setIsOpen(prev => !prev);
                }}
            >
                {isOpen ? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-[1em] h-[1em] text-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        aria-label="Collapse menu"
                    >
                        <title>Collapse menu</title>
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M5 12l14 0" />
                    </svg>
                ) : (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-[1em] h-[1em] text-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        aria-label="Expand menu"
                    >
                        <title>Expand menu</title>
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M12 5l0 14" />
                        <path d="M5 12l14 0" />
                    </svg>
                )}
            </div>
        );
    };

    // Render nested children if open.
    const renderChildren = () => {
        if (!isOpen) return null;

        if (children) {
            return <>{children}</>;
        }

        if (item.children && item.children.length > 0) {
            return (
                <ul className="mt-1 -mx-5">
                    {item.children.map(child => (
                        <MenuItem key={child.id} item={child as MenuItemModel} onClick={onClick} />
                    ))}
                </ul>
            );
        }
        return null;
    };

    return (
        <li className={cn('px-5 transition list-none cursor-pointer text-sm py-2', className, { 'bg-gray-200 dark:bg-gray-800': isOpen, 'pb-0': isOpen })}>
            <div className="flex items-center justify-between">
                {renderTitle()}
                {hasNestedContent && renderToggleButton()}
            </div>
            {renderChildren()}
        </li>
    );
};

export default memo(MenuItem, propsEqualityFn);
